import { useCallback, useEffect, useState } from "react";
import { Permissions, usePermissions } from "./usePermissions";
import { useRouting } from "./useRouting";

enum SearchParams {
  CREATE = 'create',
  EDIT = 'edit',
  UPDATE_STATUS = 'edit',
  ORIGIN = 'origin',
  ERROR = 'error',
  SUCCESS = 'success',
}

type PermissionValidation = (permissions: Permissions) => boolean;

interface Action {
  canExecute: PermissionValidation;
  execute: any;
  messages?: {
    error?: string;
  };
  onDismiss?: any;
  onSuccess?: any;
  onError?: any;
  clean?: any;
}

interface UseActionsProps {
  create?: Action;
  edit?: Action;
}

export const useActions = (actions: UseActionsProps) => {
  const [item, setItem] = useState<any>();
  const { tryAction } = usePermissions();
  const { getSearchParam, searchParamsUpdated, setSearchParams, cleanSearchParams } = useRouting([]);

  useEffect(() => {
    if (getSearchParam(SearchParams.CREATE) === 'true' && actions.create) tryCreate(actions.create);
    if (getSearchParam(SearchParams.EDIT) === 'true' && actions.edit) tryEdit(actions.edit, item);
  }, [searchParamsUpdated]);

  const handleCloseForm = (
    {
      completed,
      error,
    }: {
      completed: boolean;
      error?: boolean;
      isEdit?: boolean;
    }) => {
      const origin = getSearchParam(SearchParams.ORIGIN);

      const action = getSearchParam(SearchParams.EDIT) === 'true' ? actions.edit : actions.create;

      if (!completed && origin) {
        window.location.href = `${origin}${error ? `?${SearchParams.ERROR}=${action?.messages?.error}` : ''}`;
      }

      if (error) {
        action?.onError();
      } else {
        if (completed) {
          setItem(undefined);
          action?.onSuccess();
        }
        cleanSearchParams(Object.values(SearchParams));
        action?.clean?.();
      }
    };

  const tryCreate = useCallback((createAction: Action) => (
    tryAction({
      testFn: createAction?.canExecute || (() => true),
      onDismiss: () => { handleCloseForm({ completed: false }) },
      onSuccess: createAction?.execute,
    })
  ), [tryAction, handleCloseForm]);

  const tryEdit = useCallback((editAction: Action, item: any) => (
    tryAction({
      testFn: editAction?.canExecute || (() => true),
      onDismiss: () => { handleCloseForm({ completed: false }) },
      onSuccess: () => editAction?.execute(item),
    })
  ), [tryAction, handleCloseForm]);

  const customAction = useCallback((action: Action) => (
      tryAction({
        testFn: action.canExecute || (() => true),
        onDismiss: () => { action.onDismiss?.() },
        onSuccess: action.execute,
      })
  ), [tryAction]);

  const openCreate = () => {
    setSearchParams({ [SearchParams.CREATE]: 'true' });
  };

  const openEdit = (item: any) => {
    setItem(item);
    setSearchParams({ [SearchParams.EDIT]: 'true' });
  };

  return {
    openCreate,
    openEdit,
    customAction,
    handleCloseForm,
  };
};
